import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { getGuestPropertyById, type GuestPropertyItem } from "../../model/guestSession";
import { propertyService } from "../../model/propertyServices";
import { GoogleMap } from "@/shared/components/common/GoogleMap";
import { Modal } from "@/shared/modal";
import styles from "./PropertyDetailPage.module.css";

import { useCommunitySafetyData } from "../../viewmodel/useCommunitySafetyData";

export const PropertyDetailPage = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"crime" | "offenders">("crime");
  const [asyncProperty, setAsyncProperty] = useState<GuestPropertyItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedSafetyItem, setSelectedSafetyItem] = useState<{
    type: "crime" | "offenders";
    data: Record<string, unknown>;
  } | null>(null);

  // 1. Check local guest storage first
  const guestProperty = useMemo(() => {
    if (!propertyId) return undefined;
    return getGuestPropertyById(propertyId);
  }, [propertyId]);

  // 2. Fetch from backend/service if not available locally
  useEffect(() => {
    if (guestProperty) {
      setAsyncProperty(guestProperty);
      return;
    }

    if (!propertyId) return;

    let isMounted = true;
    setIsLoading(true);

    const loadPropertyDetails = async () => {
      try {
        // Try getting property details by backend ID
        const apiProp = await propertyService.getPropertyById(propertyId);
        if (apiProp && isMounted) {
          const raw = (apiProp as unknown as Record<string, unknown>).rawDetails as
            | Record<string, unknown>
            | undefined;
          const assessedVal = apiProp.tax_info?.assessed_value ?? apiProp.assessed_value ?? 0;
          const marketVal = apiProp.tax_info?.market_value ?? apiProp.estimated_value ?? 0;
          const savings =
            apiProp.amount_saved ??
            (assessedVal > marketVal && marketVal > 0
              ? Math.round((assessedVal - marketVal) * 0.025)
              : 0);
          const savingsPct =
            assessedVal > 0 && savings > 0
              ? Number(((savings / assessedVal) * 100).toFixed(2))
              : undefined;

          const mapped: GuestPropertyItem = {
            property_id: apiProp.property_id || propertyId,
            address: apiProp.address?.line1 || apiProp.address?.full_address || "Property Details",
            streetAddress: apiProp.address?.line1 || "",
            cityStateZip: [apiProp.address?.city, apiProp.address?.state, apiProp.address?.zip]
              .filter(Boolean)
              .join(", "),
            city: apiProp.address?.city,
            state: apiProp.address?.state,
            zip: apiProp.address?.zip,
            assessedValue: assessedVal,
            marketValue: marketVal,
            potentialSavings: savings,
            potentialSavingsPercent: savingsPct,
            propertyType: apiProp.property_type || (raw?.propertyType as string) || undefined,
            yearBuilt: apiProp.year_built || (raw?.yearBuilt as number) || undefined,
            parcelId: apiProp.apn || (raw?.apn as string) || undefined,
            taxYear: apiProp.tax_info?.tax_year || (raw?.taxYear as number) || undefined,
            squareFeet: apiProp.square_feet || (raw?.squareFeet as number) || undefined,
            selected: true,
            latitude: apiProp.address?.latitude || (raw?.latitude as number) || undefined,
            longitude: apiProp.address?.longitude || (raw?.longitude as number) || undefined,
            ownerDetails: {
              ownerName: apiProp.owner?.full_name,
              phone: apiProp.owner?.phone || undefined,
            },
            paymentStatus: apiProp.payment_status || (raw?.payment_status as string) || undefined,
          };
          setAsyncProperty(mapped);
        }
      } catch (err) {
        console.warn("Failed to fetch property by ID, falling back to default property info:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadPropertyDetails();

    return () => {
      isMounted = false;
    };
  }, [propertyId, guestProperty]);

  // Combined property data
  const property = guestProperty || asyncProperty;

  // Extract dynamic details
  const displayData = useMemo(() => {
    const raw = (property?.rawDetails || {}) as Record<string, unknown>;
    const propInfo = (raw.propertyInfo || {}) as Record<string, unknown>;
    const propAddr = (propInfo.address || {}) as Record<string, unknown>;
    const taxInfo = (raw.taxInfo || {}) as Record<string, unknown>;
    const ownerInfo = (raw.ownerInfo || {}) as Record<string, unknown>;
    const lotInfo = (raw.lotInfo || {}) as Record<string, unknown>;
    const neighborhood = (raw.neighborhood || {}) as Record<string, unknown>;

    const rawAddr = (
      typeof raw.address === "object" && raw.address !== null ? raw.address : {}
    ) as Record<string, unknown>;

    // Latitude & Longitude extraction
    const lat =
      property?.latitude ??
      (propInfo.latitude as number | string | undefined) ??
      (propAddr.latitude as number | string | undefined) ??
      (rawAddr.latitude as number | string | undefined) ??
      (raw.latitude as number | string | undefined) ??
      (property?.address && typeof property.address === "object"
        ? (property.address as Record<string, unknown>).latitude
        : undefined);

    const lon =
      property?.longitude ??
      (propInfo.longitude as number | string | undefined) ??
      (propAddr.longitude as number | string | undefined) ??
      (rawAddr.longitude as number | string | undefined) ??
      (raw.longitude as number | string | undefined) ??
      (property?.address && typeof property.address === "object"
        ? (property.address as Record<string, unknown>).longitude
        : undefined);

    // Valuation calculations
    const assessed =
      property?.assessedValue ??
      (taxInfo.assessedValue as number | undefined) ??
      (raw.estimatedValue as number | undefined) ??
      0;
    const market =
      property?.marketValue ??
      (taxInfo.marketValue as number | undefined) ??
      (raw.estimatedValue as number | undefined) ??
      0;
    const savings =
      property?.potentialSavings ??
      (assessed > market && market > 0 ? Math.round((assessed - market) * 0.025) : 0);
    const savingsPct =
      property?.potentialSavingsPercent ??
      (assessed > 0 && savings > 0 ? Number(((savings / assessed) * 100).toFixed(2)) : undefined);

    // Owner name extraction
    const owner1Group = ownerInfo.owner1Group as Record<string, unknown> | undefined;
    const owner1FullName =
      property?.ownerDetails?.ownerName ||
      (owner1Group?.fullname as string) ||
      (ownerInfo.owner1FullName as string) ||
      (ownerInfo.owner1FirstName
        ? `${ownerInfo.owner1FirstName} ${ownerInfo.owner1LastName || ""}`.trim()
        : undefined);

    // Mailing address extraction
    const mailAddrObj = ownerInfo.mailAddress as Record<string, unknown> | undefined;
    const mailingAddress =
      property?.ownerDetails?.mailingAddress ||
      (mailAddrObj
        ? [mailAddrObj.street, mailAddrObj.city, mailAddrObj.state, mailAddrObj.zip]
            .filter(Boolean)
            .join(", ")
        : undefined);

    // Lot size extraction
    const lotSqFt = lotInfo.lotSizeSquareFeet as number | undefined;
    const lotAcres = lotInfo.lotSizeAcres as number | undefined;
    const lotSizeStr = property?.lotSize
      ? String(property.lotSize)
      : lotSqFt
        ? `${lotSqFt.toLocaleString()} sqft`
        : lotAcres
          ? `${lotAcres} Acres`
          : undefined;

    // Square feet extraction
    const livingSqFt = propInfo.livingSquareFeet as number | undefined;
    const bldgSqFt = propInfo.buildingSquareFeet as number | undefined;
    const sqFtStr = property?.squareFeet
      ? String(property.squareFeet)
      : livingSqFt
        ? `${livingSqFt.toLocaleString()} sqft`
        : bldgSqFt
          ? `${bldgSqFt.toLocaleString()} sqft`
          : undefined;

    return {
      property_id: property?.property_id || propertyId || "prop_detail",
      address:
        property?.address ||
        [property?.streetAddress, property?.cityStateZip].filter(Boolean).join(", ") ||
        "Property Details",
      streetAddress: property?.streetAddress || property?.address || "",
      city: property?.city,
      state: property?.state,
      zip: property?.zip,
      latitude: lat !== undefined && lat !== null && !isNaN(Number(lat)) ? Number(lat) : undefined,
      longitude: lon !== undefined && lon !== null && !isNaN(Number(lon)) ? Number(lon) : undefined,
      assessedValue: assessed,
      marketValue: market,
      potentialSavings: savings,
      potentialSavingsPercent: savingsPct,
      propertyType:
        property?.propertyType ||
        (raw.propertyType as string) ||
        (propInfo.propertyType as string) ||
        undefined,
      yearBuilt:
        property?.yearBuilt || (propInfo.yearBuilt as number | string | undefined) || undefined,
      parcelId:
        property?.parcelId || (propInfo.apn as string) || (taxInfo.apn as string) || undefined,
      lotSize: lotSizeStr,
      taxYear: property?.taxYear || (taxInfo.taxYear as number | string | undefined) || undefined,
      squareFeet: sqFtStr,
      schoolDistrict:
        property?.schoolDistrict || (neighborhood.schoolDistrict as string) || undefined,
      ownerDetails: {
        ownerName: owner1FullName,
        email: property?.ownerDetails?.email,
        mailingAddress,
        phone: property?.ownerDetails?.phone,
      },
      rawDetails: property?.rawDetails,
      selected: property?.selected ?? true,
      paymentStatus: property?.paymentStatus || (raw.payment_status as string) || undefined,
    };
  }, [property, propertyId]);

  const isPaid = displayData.paymentStatus === "paid" || displayData.paymentStatus === "completed";

  const addressData = useMemo(() => {
    return {
      addressString: displayData.address,
      address: {
        id: displayData.property_id,
        address: displayData.streetAddress,
        city: displayData.city,
        state: displayData.state,
        zipCode: displayData.zip,
        latitude: displayData.latitude,
        longitude: displayData.longitude,
      },
    };
  }, [displayData]);

  const safePropertyDetails = useMemo(() => {
    return (displayData.rawDetails || {
      latitude: displayData.latitude,
      longitude: displayData.longitude,
      propertyInfo: {
        latitude: displayData.latitude,
        longitude: displayData.longitude,
      },
    }) as unknown as import("../../model/propertiesTypes").PropertyRealStateDetailInfo;
  }, [displayData.rawDetails, displayData.latitude, displayData.longitude]);

  const {
    sexOffendersData,
    crimeIncidentsData,
    loading: safetyLoading,
    mapMarkers,
    getCoordinates,
  } = useCommunitySafetyData({
    propertyDetails: safePropertyDetails,
    addressData,
    activeTab,
    distance: 5000,
  });

  const mapCenter = useMemo(() => {
    const coords = getCoordinates();
    return coords ? { lat: coords.lat, lng: coords.lon } : undefined;
  }, [getCoordinates]);

  const records = useMemo((): Array<{
    id: string;
    name: string;
    meta: string;
    imageUrl?: string;
    originalData: Record<string, unknown>;
  }> => {
    if (activeTab === "crime") {
      const incidents = crimeIncidentsData?.incidents || [];
      if (incidents.length > 0) {
        return incidents.map((i, idx) => ({
          id: `crime_${idx}_${i.incident_code || idx}`,
          name: i.incident_offense_description || i.incident_offense || "Crime Incident",
          meta: i.incident_address || i.incident_source_name || "Reported Incident",
          originalData: i as unknown as Record<string, unknown>,
        }));
      }
      return mapMarkers
        .filter((m) => m.type === "crime")
        .map((m, idx) => {
          const d = ((m.data as unknown as Record<string, unknown>) || {}) as Record<
            string,
            unknown
          >;
          return {
            id: `crime_marker_${idx}`,
            name:
              (d?.incident_offense as string) ||
              (d?.incident_offense_description as string) ||
              "Property Incident Report",
            meta: (d?.incident_address as string) || "Nearby Location",
            originalData: d,
          };
        });
    }

    const offenders = sexOffendersData?.sex_offenders || [];
    if (offenders.length > 0) {
      return offenders.map((o, idx) => ({
        id: `offender_${idx}_${o.sex_offender_id || idx}`,
        name:
          o.sex_offender_name ||
          [o.sex_offender_first_name, o.sex_offender_last_name].filter(Boolean).join(" ") ||
          "Sex Offender",
        meta:
          [o.sex_offender_sex, o.sex_offender_age ? `Age ${o.sex_offender_age}` : null]
            .filter(Boolean)
            .join(" · ") || "Registered Offender",
        imageUrl: o.sex_offender_image_url || undefined,
        originalData: o as unknown as Record<string, unknown>,
      }));
    }

    return mapMarkers
      .filter((m) => m.type === "offender")
      .map((m, idx) => {
        const d = ((m.data as unknown as Record<string, unknown>) || {}) as Record<string, unknown>;
        return {
          id: `offender_marker_${idx}`,
          name: (d?.sex_offender_name as string) || "Registered Offender",
          meta:
            [
              (d?.sex_offender_charges as string) || "Class B Offender",
              d?.sex_offender_age ? `Age ${d.sex_offender_age}` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "Registered Offender",
          imageUrl: (d?.sex_offender_image_url as string) || undefined,
          originalData: d,
        };
      });
  }, [activeTab, crimeIncidentsData?.incidents, sexOffendersData?.sex_offenders, mapMarkers]);

  // Modal map data computation
  const modalMapData = useMemo(() => {
    if (!selectedSafetyItem) return null;

    const data = selectedSafetyItem.data;
    const isCrime = selectedSafetyItem.type === "crime";

    const latVal = isCrime
      ? Number(data.incident_latitude || data.latitude || displayData.latitude || 39.4153)
      : Number(data.sex_offender_lat || data.latitude || displayData.latitude || 39.4153);

    const lngVal = isCrime
      ? Number(data.incident_longitude || data.longitude || displayData.longitude || -81.4554)
      : Number(data.sex_offender_lon || data.longitude || displayData.longitude || -81.4554);

    const markerItem: import("../../model/propertiesTypes").CommunitySafetyMapMarker = isCrime
      ? {
          lat: latVal,
          lng: lngVal,
          color: "#ef4444",
          type: "crime",
          data: data as unknown as import("../../model/propertiesTypes").CrimeIncident,
        }
      : {
          lat: latVal,
          lng: lngVal,
          color: "#eab308",
          type: "offender",
          data: data as unknown as import("../../model/propertiesTypes").SexOffender,
        };

    return {
      center: { lat: latVal, lng: lngVal },
      markers: [markerItem],
      address: isCrime
        ? (data.incident_address as string) || displayData.address
        : (data.sex_offender_address_line1 as string) || displayData.address,
    };
  }, [selectedSafetyItem, displayData.address, displayData.latitude, displayData.longitude]);

  const handleBackToProperties = () => {
    navigate(routes.properties);
  };

  const handleFileProtest = () => {
    const singleProperty = [displayData];
    localStorage.setItem("protest_selected_properties", JSON.stringify(singleProperty));
    navigate(routes.protestPayment, {
      state: {
        selectedProperties: singleProperty,
      },
    });
  };

  if (isLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.loadingWrapper}>Loading Property Details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Back Link */}
        <button type="button" className={styles.backLink} onClick={handleBackToProperties}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Properties
        </button>

        {/* Header Section with Title and Dynamic Coordinates */}
        <div className={styles.headerWrapper}>
          <h1 className={styles.propertyTitle}>{displayData.address}</h1>
          <div className={styles.coordinatesBadge}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                fill="currentColor"
              />
            </svg>
            <span>
              Lat: {displayData.latitude !== undefined ? displayData.latitude.toFixed(6) : "N/A"},{" "}
              Lng: {displayData.longitude !== undefined ? displayData.longitude.toFixed(6) : "N/A"}
            </span>
          </div>
        </div>

        {/* 2-Column Layout */}
        <div className={styles.twoColumnLayout}>
          {/* Left Column */}
          <div className={styles.leftColumn}>
            {/* Valuation Summary Card */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Valuation Summary</h2>
              <div className={styles.metricsGrid}>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Assessed Value</span>
                  <span className={styles.metricValue}>
                    {displayData.assessedValue
                      ? `$${displayData.assessedValue.toLocaleString()}`
                      : "N/A"}
                  </span>
                </div>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Market Value</span>
                  <span className={styles.metricValue}>
                    {displayData.marketValue
                      ? `$${displayData.marketValue.toLocaleString()}`
                      : "N/A"}
                  </span>
                </div>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Potential Savings</span>
                  <div className={styles.metricValueGreen}>
                    <span>
                      {displayData.potentialSavings
                        ? `$${displayData.potentialSavings.toLocaleString()}`
                        : "$0"}
                    </span>
                    {displayData.potentialSavingsPercent ? (
                      <span className={styles.savingsPercent}>
                        ({displayData.potentialSavingsPercent}%)
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Property Information Card */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Property Information</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Latitude</span>
                  <span className={styles.infoVal}>
                    {displayData.latitude !== undefined ? displayData.latitude.toFixed(6) : "N/A"}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Longitude</span>
                  <span className={styles.infoVal}>
                    {displayData.longitude !== undefined ? displayData.longitude.toFixed(6) : "N/A"}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Property Type</span>
                  <span className={styles.infoVal}>{displayData.propertyType || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Owner</span>
                  <span className={styles.infoVal}>
                    {displayData.ownerDetails?.ownerName || "N/A"}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Year Built</span>
                  <span className={styles.infoVal}>{displayData.yearBuilt || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Parcel ID</span>
                  <span className={styles.infoVal}>{displayData.parcelId || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Lot Size</span>
                  <span className={styles.infoVal}>{displayData.lotSize || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Tax Year</span>
                  <span className={styles.infoVal}>{displayData.taxYear || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Square Feet</span>
                  <span className={styles.infoVal}>{displayData.squareFeet || "N/A"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>School District</span>
                  <span className={styles.infoVal}>{displayData.schoolDistrict || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Owner Details Card */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Owner Details</h2>
              <div className={styles.ownerGrid}>
                <div className={styles.ownerItem}>
                  <div className={styles.iconCircle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className={styles.ownerDetailsText}>
                    <span className={styles.ownerLabel}>Owner Name</span>
                    <span className={styles.ownerVal}>
                      {displayData.ownerDetails?.ownerName || "N/A"}
                    </span>
                  </div>
                </div>

                <div className={styles.ownerItem}>
                  <div className={styles.iconCircle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className={styles.ownerDetailsText}>
                    <span className={styles.ownerLabel}>Email Address</span>
                    <span className={styles.ownerVal}>
                      {displayData.ownerDetails?.email || "N/A"}
                    </span>
                  </div>
                </div>

                <div className={styles.ownerItem}>
                  <div className={styles.iconCircle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className={styles.ownerDetailsText}>
                    <span className={styles.ownerLabel}>Mailing Address</span>
                    <span className={styles.ownerVal}>
                      {displayData.ownerDetails?.mailingAddress || "N/A"}
                    </span>
                  </div>
                </div>

                <div className={styles.ownerItem}>
                  <div className={styles.iconCircle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className={styles.ownerDetailsText}>
                    <span className={styles.ownerLabel}>Phone Number</span>
                    <span className={styles.ownerVal}>
                      {displayData.ownerDetails?.phone || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Property Savings Details Card */}
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Property Savings Details</h2>
              <div className={styles.metricsGrid}>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Assessed Value</span>
                  <span className={styles.metricValue}>
                    {displayData.assessedValue
                      ? `$${displayData.assessedValue.toLocaleString()}`
                      : "N/A"}
                  </span>
                </div>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Estimated Market Value</span>
                  <span className={styles.metricValue}>
                    {displayData.marketValue
                      ? `$${displayData.marketValue.toLocaleString()}`
                      : "N/A"}
                  </span>
                </div>
                <div className={styles.metricBox}>
                  <span className={styles.metricLabel}>Potential Savings</span>
                  <div className={styles.metricValueGreen}>
                    <span>
                      {displayData.potentialSavings
                        ? `$${displayData.potentialSavings.toLocaleString()}`
                        : "$0"}
                    </span>
                    {displayData.potentialSavingsPercent ? (
                      <span className={styles.savingsPercent}>
                        ({displayData.potentialSavingsPercent}%)
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Community Safety */}
          <div className={styles.rightColumn}>
            <div className={styles.card}>
              <div className={styles.safetyHeader}>
                <div className={styles.safetyTitle}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 1L2 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-4z"
                      fill="#10b981"
                    />
                  </svg>
                  Community Safety
                </div>
                <span className={styles.safeAreaTag}>Safe Area</span>
              </div>

              {/* Tabs */}
              <div className={styles.toggleButtons}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${activeTab === "crime" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setActiveTab("crime")}
                >
                  Crime Report
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${activeTab === "offenders" ? styles.toggleBtnActive : ""}`}
                  onClick={() => setActiveTab("offenders")}
                >
                  Sex Offenders
                </button>
              </div>

              {/* Status Message */}
              <p className={styles.incidentNotice}>
                {safetyLoading
                  ? "Loading community safety records..."
                  : activeTab === "crime"
                    ? crimeIncidentsData?.incidents_count || mapMarkers.length
                      ? `${crimeIncidentsData?.incidents_count || mapMarkers.length} crime incidents found in this area`
                      : "No crime incidents found in this area"
                    : sexOffendersData?.sex_offenders_count || mapMarkers.length
                      ? `${sexOffendersData?.sex_offenders_count || mapMarkers.length} sex offenders found in this area`
                      : "No sex offenders found in this area"}
              </p>

              {/* Map Box */}
              <div className={styles.mapBox} style={{ position: "relative" }}>
                {!isPaid && (
                  <div className={styles.lockedOverlay}>
                    <div className={styles.lockIcon}>🔒</div>
                    {/* <div className={styles.lockedTitle}>Unlock after payment</div> */}
                    <div className={styles.lockedSubtitle}>Complete your Payment to unlock</div>
                  </div>
                )}
                <div
                  style={{
                    filter: !isPaid ? "blur(4px)" : "none",
                    pointerEvents: !isPaid ? "none" : "auto",
                    height: "100%",
                  }}
                >
                  <GoogleMap
                    address={displayData.address}
                    center={mapCenter}
                    zoom={14}
                    markers={mapMarkers}
                  />
                </div>
              </div>

              {/* Recent Records List */}
              <div className={styles.recordsHeader}>
                <div className={styles.recordsTitle}>
                  {activeTab === "crime" ? "Crime Reports" : "Sex Offenders"}
                </div>
                <span className={styles.recordsCountBadge}>{records.length} found</span>
              </div>

              <div className={styles.recordsList} style={{ position: "relative" }}>
                {!isPaid && (
                  <div className={styles.lockedOverlay}>
                    <div className={styles.lockIcon}>🔒</div>
                    {/* <div className={styles.lockedTitle}>Unlock after payment</div> */}
                    <div className={styles.lockedSubtitle}>Complete your Payment to unlock</div>
                  </div>
                )}
                <div
                  style={{
                    filter: !isPaid ? "blur(4px)" : "none",
                    pointerEvents: !isPaid ? "none" : "auto",
                  }}
                >
                  {safetyLoading && (
                    <div className={styles.noRecordsText}>Loading safety records...</div>
                  )}
                  {!safetyLoading && records.length === 0 && (
                    <div className={styles.noRecordsText}>No records found in this area.</div>
                  )}
                  {!safetyLoading &&
                    records.map((record) => (
                      <div
                        key={record.id}
                        className={styles.recordItemClickable}
                        onClick={() =>
                          setSelectedSafetyItem({
                            type: activeTab,
                            data: record.originalData,
                          })
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setSelectedSafetyItem({
                              type: activeTab,
                              data: record.originalData,
                            });
                          }
                        }}
                      >
                        <div className={styles.avatar}>
                          {activeTab === "offenders" && record.imageUrl ? (
                            <img
                              src={record.imageUrl}
                              alt={record.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            record.name
                              .split(" ")
                              .map((part) => part.charAt(0))
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)
                          )}
                        </div>
                        <div className={styles.recordContent}>
                          <div className={styles.recordName} title={record.name}>
                            {record.name}
                          </div>
                          <div className={styles.recordMeta} title={record.meta}>
                            {record.meta}
                          </div>
                        </div>
                        <span className={styles.previewTag}>Preview →</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Community Data Summary */}
              <div className={styles.communityDataSection} style={{ position: "relative" }}>
                {!isPaid && (
                  <div className={styles.lockedOverlay}>
                    <div className={styles.lockIcon}>🔒</div>
                    {/* <div className={styles.lockedTitle}>Unlock after payment</div> */}
                    <div className={styles.lockedSubtitle}>Complete your Payment to unlock</div>
                  </div>
                )}
                <div
                  style={{
                    filter: !isPaid ? "blur(4px)" : "none",
                    pointerEvents: !isPaid ? "none" : "auto",
                  }}
                >
                  <h3 className={styles.communityDataTitle}>Community Summary</h3>
                  <span className={styles.communityItem}>
                    {crimeIncidentsData?.incidents_count ||
                      (activeTab === "crime" ? mapMarkers.length : 0)}{" "}
                    crime incidents
                  </span>
                  <span className={styles.communityItem}>
                    {sexOffendersData?.sex_offenders_count ||
                      (activeTab === "offenders" ? mapMarkers.length : 0)}{" "}
                    sex offenders
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Button */}
        <div className={styles.bottomActionWrapper}>
          <button type="button" className={styles.fileProtestBtn} onClick={handleFileProtest}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            File Protest for This Property
          </button>
        </div>

        {/* Safety Item Preview Modal */}
        <Modal
          isOpen={Boolean(selectedSafetyItem)}
          onClose={() => setSelectedSafetyItem(null)}
          title={
            selectedSafetyItem?.type === "crime" ? "Crime Incident Details" : "Sex Offender Details"
          }
        >
          {selectedSafetyItem && (
            <div className={styles.modalContainer}>
              {/* Google Map Preview inside Modal */}
              {modalMapData && (
                <div className={styles.modalMapBox}>
                  <GoogleMap
                    address={modalMapData.address}
                    center={modalMapData.center}
                    zoom={15}
                    markers={modalMapData.markers}
                  />
                </div>
              )}

              {selectedSafetyItem.type === "crime" ? (
                <>
                  <div className={styles.modalHeaderRow}>
                    <div
                      className={styles.modalAvatar}
                      style={{ backgroundColor: "#fee2e2", color: "#ef4444" }}
                    >
                      🚨
                    </div>
                    <div className={styles.modalHeaderMeta}>
                      <h3 className={styles.modalTitle}>
                        {(selectedSafetyItem.data.incident_offense_description as string) ||
                          (selectedSafetyItem.data.incident_offense as string) ||
                          "Crime Incident"}
                      </h3>
                      <span className={styles.modalSubtitle}>
                        {(selectedSafetyItem.data.incident_address as string) ||
                          (selectedSafetyItem.data.incident_source_name as string) ||
                          displayData.address}
                      </span>
                    </div>
                  </div>

                  <div className={styles.modalDetailsGrid}>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Incident Code</span>
                      <span className={styles.modalDetailValue}>
                        {(selectedSafetyItem.data.incident_code as string) || "N/A"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Offense Category</span>
                      <span className={styles.modalDetailValue}>
                        {(selectedSafetyItem.data.incident_offense_crime_against as string) ||
                          (selectedSafetyItem.data.incident_offense as string) ||
                          "N/A"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Reported Date</span>
                      <span className={styles.modalDetailValue}>
                        {selectedSafetyItem.data.incident_date
                          ? new Date(String(selectedSafetyItem.data.incident_date)).toLocaleString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Source Agency</span>
                      <span className={styles.modalDetailValue}>
                        {(selectedSafetyItem.data.incident_source_name as string) || "Local Agency"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Latitude</span>
                      <span className={styles.modalDetailValue}>
                        {String(
                          selectedSafetyItem.data.incident_latitude ||
                            selectedSafetyItem.data.latitude ||
                            "N/A"
                        )}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Longitude</span>
                      <span className={styles.modalDetailValue}>
                        {String(
                          selectedSafetyItem.data.incident_longitude ||
                            selectedSafetyItem.data.longitude ||
                            "N/A"
                        )}
                      </span>
                    </div>
                  </div>

                  {(selectedSafetyItem.data.incident_offense_detail_description as string) && (
                    <div className={styles.modalDescriptionBox}>
                      <div className={styles.modalDescriptionLabel}>Detailed Description</div>
                      <div className={styles.modalDescriptionText}>
                        {selectedSafetyItem.data.incident_offense_detail_description as string}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={styles.modalHeaderRow}>
                    <div className={styles.modalAvatar}>
                      {(selectedSafetyItem.data.sex_offender_image_url as string) ? (
                        <img
                          src={selectedSafetyItem.data.sex_offender_image_url as string}
                          alt="Offender"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        "👤"
                      )}
                    </div>
                    <div className={styles.modalHeaderMeta}>
                      <h3 className={styles.modalTitle}>
                        {(selectedSafetyItem.data.sex_offender_name as string) ||
                          [
                            selectedSafetyItem.data.sex_offender_first_name as string,
                            selectedSafetyItem.data.sex_offender_last_name as string,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                          "Registered Offender"}
                      </h3>
                      <span className={styles.modalSubtitle}>
                        {(selectedSafetyItem.data.sex_offender_charges as string) ||
                          "Class B Offender"}
                      </span>
                      {(Boolean(selectedSafetyItem.data.sex_offender_is_predator === "true") ||
                        Boolean(selectedSafetyItem.data.sex_offender_is_predator === 1)) && (
                        <span className={styles.predatorBadge}>Sexual Predator</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.modalDetailsGrid}>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Age</span>
                      <span className={styles.modalDetailValue}>
                        {String(selectedSafetyItem.data.sex_offender_age || "N/A")}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Gender</span>
                      <span className={styles.modalDetailValue}>
                        {String(selectedSafetyItem.data.sex_offender_sex || "N/A")}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Race</span>
                      <span className={styles.modalDetailValue}>
                        {String(selectedSafetyItem.data.sex_offender_race || "N/A")}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Height & Weight</span>
                      <span className={styles.modalDetailValue}>
                        {[
                          selectedSafetyItem.data.sex_offender_height,
                          selectedSafetyItem.data.sex_offender_weight,
                        ]
                          .filter(Boolean)
                          .join(" / ") || "N/A"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Hair / Eye Color</span>
                      <span className={styles.modalDetailValue}>
                        {[
                          selectedSafetyItem.data.sex_offender_hair,
                          selectedSafetyItem.data.sex_offender_eyes,
                        ]
                          .filter(Boolean)
                          .join(" / ") || "N/A"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Address / Area</span>
                      <span className={styles.modalDetailValue}>
                        {(selectedSafetyItem.data.sex_offender_address_line1 as string) ||
                          (selectedSafetyItem.data.sex_offender_location as string) ||
                          "Address on File"}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Latitude</span>
                      <span className={styles.modalDetailValue}>
                        {String(
                          selectedSafetyItem.data.sex_offender_lat ||
                            selectedSafetyItem.data.latitude ||
                            "N/A"
                        )}
                      </span>
                    </div>
                    <div className={styles.modalDetailBox}>
                      <span className={styles.modalDetailLabel}>Longitude</span>
                      <span className={styles.modalDetailValue}>
                        {String(
                          selectedSafetyItem.data.sex_offender_lon ||
                            selectedSafetyItem.data.longitude ||
                            "N/A"
                        )}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};
